<template>
  <div class="opentok-publisher" @click="toolbarOpacity = 1">
    <div
      class="buttons-overlay-container d-flex flex-row justify-content-start align-items-center flex-wrap"
      :style="`opacity: ${toolbarOpacity};`"
      @mouseover="showToolbar"
      @mouseleave="startHidingToolbar"
    >
      <button
        class="btn bg-coral d-flex justify-content-center align-items-center mr-3 mb-2"
        @click="stopPublishing"
        v-tooltip="{
          content: streamingTooltip,
          delay: 100,
          placement: 'top',
          container: 'body',
        }"
      >
        <i class="far fa-phone c-white"></i>
      </button>
      <button
        class="btn bg-charcoal d-flex justify-content-center align-items-center mr-3 mb-2"
        @click="toggleCamera"
        v-tooltip="{
          content: cameraTooltip,
          delay: 100,
          placement: 'top',
          container: 'body',
        }"
      >
        <i v-if="publishVideo" class="fal fa-video c-white"></i>
        <i v-else class="fal fa-video-slash c-white"></i>
      </button>
      <button
        class="btn bg-charcoal d-flex justify-content-center align-items-center mr-3 mb-2"
        @click="toggleMic"
        v-tooltip="{
          content: microphoneTooltip,
          delay: 100,
          placement: 'top',
          container: 'body',
        }"
      >
        <i v-if="publishAudio" class="fal fa-microphone c-white"></i>
        <i v-else class="fal fa-microphone-slash c-white"></i>
      </button>
      <button
        class="btn recording d-flex justify-content-center align-items-center mr-1 mr-md-3 mb-2"
        :class="{ active: isRecording }"
        :disabled="isStartStopSessionInProgress"
        @click="toggleRecording"
        v-tooltip="{
          content: recordingTooltip,
          delay: 100,
          placement: 'top',
          container: 'body',
        }"
      >
        <span class="ml-2" v-if="isRecording">{{
          $t('EVENTS.BUTTON.STOP_RECORDING')
        }}</span>
        <span class="ml-2" v-else>{{
          $t('EVENTS.BUTTON.START_RECORDING')
        }}</span>
      </button>
      <button
        id="attendees-btn"
        class="btn attendees d-flex justify-content-center align-items-center mb-2"
      >
        <i class="fal fa-users c-dark-gray"></i>
        <span class="ml-2"
          >{{ $t('EVENTS.LABEL.ATTENDEES') }} {{ membersList.length }}</span
        >
      </button>
      <HMPopover target="attendees-btn" placement="top">
        <div class="members-list">
          <i class="fal fa-users c-dark-gray"></i>
          <span class="ml-2"
            >{{ $t('EVENTS.LABEL.ATTENDEES') }} {{ membersList.length }}</span
          >
          <hr />
          <HMSearch v-model="attendeeSearch" />
          <div class="my-5 text-center" v-if="filteredMembersList.length == 0">
            {{ $t('EVENTS.LABEL.NO_ATTENDEES') }}
          </div>
          <ul v-else class="list-unstyled mt-2">
            <li
              v-for="member in filteredMembersList"
              :key="member.user.id"
              class=""
            >
              <img
                v-if="member.user.image"
                class="img-circle rounded"
                height="16px"
                :src="member.user.image"
              />
              {{ member.user.name }}
            </li>
          </ul>
          <div class="py-5"></div>
        </div>
      </HMPopover>
    </div>
    <OpenTokInStreamSetup
      v-if="publisherStream && !isSetupClosed"
      class="setup-container m-0 p-0 h-100"
      :class="{ closed: isSetupClosed }"
      :stream="publisherStream"
      @devicesLoaded="devicesLoaded"
    />
  </div>
</template>

<script>
import OT from '@opentok/client';
import HMPopover from '@/web/components/common/HMPopover';
import HMSearch from '@/web/components/common/HMSearch';
import OpenTokInStreamSetup from '@/web/views/admin/events/streaming/OpenTokInStreamSetup';
import { mapGetters } from 'vuex';

export default {
  name: 'OpenTokPublisher',
  components: {
    HMPopover,
    HMSearch,
    OpenTokInStreamSetup,
  },
  props: {
    session: {
      type: OT.Session,
      required: false,
    },
    opts: {
      type: Object,
      required: false,
    },
    membersList: {
      type: Array,
      default: () => [],
    },
    isStartStopSessionInProgress: {
      type: Boolean,
    },
    isRecording: {
      type: Boolean,
    },
    isSetupClosed: {
      type: Boolean,
    },
    logoUrl: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      streamingProps: 'setting/streamingProps',
    }),
    filteredMembersList() {
      if (this.attendeeSearch === '') return this.membersList;
      return this.membersList.filter(member => {
        const result = member.user.name.search(
          new RegExp(this.attendeeSearch, 'i')
        );
        return result > -1;
      });
    },
    recordingTooltip() {
      return this.isRecording
        ? this.$t('EVENTS.TEXT.CLICK_HERE_TO_STOP_RECORDING')
        : this.$t('EVENTS.TEXT.CLICK_HERE_TO_START_RECORDING');
    },
    microphoneTooltip() {
      return this.publishAudio
        ? this.$t('EVENTS.TEXT.CLICK_HERE_TO_MUTE_YOUR_MICROFONE')
        : this.$t('EVENTS.TEXT.CLICK_HERE_TO_UNMUTE_YOUR_MICROFONE');
    },
    cameraTooltip() {
      return this.publishVideo
        ? this.$t('EVENTS.TEXT.CLICK_HERE_TO_TURN_OFF_YOUR_CAMERA')
        : this.$t('EVENTS.TEXT.CLICK_HERE_TO_TURN_ON_YOUR_CAMERA');
    },
    streamingTooltip() {
      return this.$t('EVENTS.TEXT.CLICK_HERE_TO_STOP_STREAMING');
    },
  },
  data() {
    return {
      publisher: null,
      publisherVideoElement: null,
      publisherStream: null,
      toolbarOpacity: 1,
      attendeeSearch: '',
      publishAudio: this.opts.publishAudio,
      publishVideo: this.opts.publishVideo,
      availableDevices: {},
    };
  },
  methods: {
    showToolbar() {
      this.toolbarOpacity = 1;
    },
    startHidingToolbar() {
      // const interval = setInterval(() => {
      //   this.toolbarOpacity -= 0.05;
      //   if (this.toolbarOpacity <= 0) {
      //     this.toolbarOpacity = 0;
      //     clearInterval(interval);
      //   }
      // }, 50);
    },
    stopPublishing() {
      this.publisher.destroy();
      this.$emit('stop');
    },
    toggleCamera() {
      this.publishVideo = !this.publishVideo;
      this.publisher.publishVideo(this.publishVideo);
      this.streamingProps.videoEnabled = this.publishVideo;
    },
    toggleMic() {
      this.publishAudio = !this.publishAudio;
      this.publisher.publishAudio(this.publishAudio);
      this.streamingProps.audioInputEnabled = this.publishAudio;
    },
    toggleRecording() {
      this.$emit('toggleRecording');
    },
    devicesLoaded(devices) {
      this.availableDevices = devices;
    },
    async videoDeviceSelectedEvent(device) {
      console.log('videoDeviceSelectedEvent', device);
      let retries = this.availableDevices.videoDevices?.length || 0;
      while (retries > 0) {
        try {
          const { deviceId } = await this.publisher.cycleVideo();
          if (deviceId === device.deviceId) {
            return;
          }
        } catch (error) {
          console.error(error);
        } finally {
          retries -= 1;
        }
      }
    },
    audioInputDeviceSelectedEvent(device) {
      this.publisher.setAudioSource(device.deviceId);
    },
    audioOutputDeviceSelectedEvent(device) {
      this.$emit('audioOutputDeviceSelectedEvent', device);
    },
  },
  mounted: function () {
    this.publisher = OT.initPublisher(this.$el, this.opts, err => {
      if (err) {
        this.$emit('error', err);
      } else {
        this.$emit('publisherCompleted');
        if (!this.publishVideo) {
          if (this.logoUrl) {
            this.publisher.setStyle('backgroundImageURI', this.logoUrl);
          }
        }
      }
    });
    this.publisher.on('videoElementCreated', event => {
      this.publisherVideoElement = event.element;
      this.publisherStream = this.publisherVideoElement.srcObject;
    });
    this.$emit('publisherCreated', this.publisher);
    const publish = () => {
      this.session.publish(this.publisher, err => {
        if (err) {
          this.$emit('error', err);
        } else {
          this.$emit('publisherConnected', this.publisher);
        }
      });
    };
    if (this.session && this.session.isConnected()) {
      publish();
    }
    if (this.session) {
      this.session.on('sessionConnected', publish);
    }
  },
  destroyed() {
    if (this.publisherStream) {
      this.publisherStream.getTracks().forEach(function (track) {
        track.stop();
      });
    }
  },
  watch: {
    'streamingProps.selectedVideoDevice': {
      handler(value) {
        this.videoDeviceSelectedEvent(value);
      },
    },
    'streamingProps.selectedAudioInputDevice': {
      handler(value) {
        this.audioInputDeviceSelectedEvent(value);
      },
    },
    'streamingProps.selectedAudioOutputDevice': {
      handler(value) {
        this.audioOutputDeviceSelectedEvent(value);
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/web/styles/components-variables.scss';

.opentok-publisher {
  width: 100%;
  height: 100%;

  /* Hide archiving bottom info bar */
  .OT_archiving.OT_edge-bar-item {
    display: none;
  }

  .buttons-overlay-container {
    padding: 2rem 1rem;
    position: absolute;
    bottom: 0;
    left: 0;
    transition: opacity ease-in-out 1s;
    width: 100%;
    z-index: 1000;

    &:hover {
      transition-duration: 400ms;
    }

    .btn {
      border-radius: 50%;
      border: none;
      height: 48px;
      width: 48px;
      cursor: pointer;

      &.attendees,
      &.recording {
        color: $dark-gray;
        background-color: $light-gray;
        border-radius: 48px;
        width: auto;
        padding: 0.5rem 1rem;
      }

      &.recording {
        &.active {
          background-color: $coral;
          color: $white;
          animation-name: pulse;
          animation-duration: 1.5s;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
        }

        @keyframes pulse {
          0% {
            box-shadow: 0px 0px 5px 0px rgba(173, 0, 0, 0.3);
          }
          65% {
            box-shadow: 0px 0px 5px 13px rgba(173, 0, 0, 0.3);
          }
          90% {
            box-shadow: 0px 0px 5px 13px rgba(173, 0, 0, 0);
          }
        }
      }
    }
  }

  .setup-container {
    position: fixed;
    top: $streaming-topbar-height;
    right: 0;
    width: 33%;
    height: unquote('calc(100vh - #{$streaming-topbar-height})');
    max-height: unquote('calc(100vh - #{$streaming-topbar-height})');
    transition: width ease-in-out 0.3s;

    @media screen and (max-width: 1200px) {
      width: 33%;
    }

    @media screen and (max-width: 992px) {
      width: 33%;
    }

    @media screen and (max-width: 768px) {
      width: 100%;
    }

    &.open {
    }

    &.closed {
      width: 0;
    }
  }
}

.members-list {
  min-height: 350px;
  min-width: 250px;
  max-width: 100%;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1rem;
}
</style>
